import { useState } from 'react';

import { format, parseISO } from 'date-fns';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Snackbar from '@zenbusiness/zui/Snackbar';
import Text from '@zenbusiness/zui/Text';

import { accountSettingsSecurity } from '#client/constants/routes';
import { useMfaMethods } from '#client/hooks/useMfaMethods';
import useMyAccountOperation from '#client/hooks/useMyAccountOperation';
import { appKeepsSignInState } from '#client/utils/appSession';
import { MY_ACCOUNT_OPERATIONS } from '#client/utils/myAccount';
import { generateMfaStepUpUrl } from '#client/utils/urls';

import { PASSKEY_PROVIDERS } from './passkeyProviders';
import * as Styled from './Passkeys.style';

import { RemoveMfaMethodDialog } from '../RemoveMfaMethodDialog';
import { MFA_STEP_UP_METHOD_PARAM, MFA_STEP_UP_PARAM, MFA_STEP_UP_REMOVE } from '../TwoStepVerification/constants';
import * as TwoStepStyled from '../TwoStepVerification/TwoStepVerification.style';

export const LOCALIZED_TEXT = {
  title: 'dashboard.accountSettings.passkeys.title',
  description: 'dashboard.accountSettings.passkeys.description',
  add: 'dashboard.accountSettings.passkeys.add',
  openInBrowser: 'dashboard.accountSettings.passkeys.openInBrowser',
  limitReached: 'dashboard.accountSettings.passkeys.limitReached',
  passkey: 'dashboard.accountSettings.passkeys.passkey',
  name: 'dashboard.accountSettings.passkeys.name',
  added: 'dashboard.accountSettings.twoStepVerification.added',
  lastUsed: 'dashboard.accountSettings.twoStepVerification.lastUsed',
  remove: 'dashboard.accountSettings.twoStepVerification.remove',
  passkeyAdded: 'dashboard.accountSettings.passkeys.added',
  startFailed: 'dashboard.accountSettings.twoStepVerification.startFailed',
};

// Auth0's limit per user.
export const MAX_PASSKEYS = 20;

const formatDate = (isoDate) => (isoDate ? format(parseISO(isoDate), 'MMM d, yyyy') : null);

// As the Enforce MFA Policy Action counts a standing factor. Without one, the forced MFA login
// would make the customer enroll SMS, so a passkey sign-in is the step-up instead.
const hasTwoStepFactor = (methods) => methods.some((m) => m.confirmed && m.type !== 'passkey' && !m.isRecoveryMethod);

// Database-connection passkeys sign the customer in instead of a password. They aren't a second
// step, so they're listed apart from two-step verification and stay when it's turned off.
function Passkeys() {
  const { formatMessage: fm } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { methods, requireMfa, passkeysAvailable, loading, error, refetch } = useMfaMethods();
  const startMyAccountOperation = useMyAccountOperation();
  const [searchParams] = useSearchParams();
  const [passkeyToRemove, setPasskeyToRemove] = useState(null);
  // Back from the forced MFA login, the removal that asked for it opens again for the user to confirm.
  // TwoStepVerification, on the same page, drops the parameters from the URL.
  const [stepUpRemovalId, setStepUpRemovalId] = useState(() =>
    searchParams.get(MFA_STEP_UP_PARAM) === MFA_STEP_UP_REMOVE ? searchParams.get(MFA_STEP_UP_METHOD_PARAM) : null,
  );
  const [startFailed, setStartFailed] = useState(false);

  if (loading) {
    return (
      <TwoStepStyled.Card2fa>
        <Skeleton height="28px" width="40%" />
        <Skeleton height="80px" />
      </TwoStepStyled.Card2fa>
    );
  }

  if (error) {
    return (
      <TwoStepStyled.Card2fa>
        <ErrorState />
      </TwoStepStyled.Card2fa>
    );
  }

  const passkeys = methods.filter((m) => m.confirmed && m.type === 'passkey');
  // Enterprise and social sign-ins can't add one, so there's nothing to show unless one exists.
  if (!passkeysAvailable && passkeys.length === 0) {
    return null;
  }
  const providerOf = (passkey) => PASSKEY_PROVIDERS[passkey.aaguid] ?? fm({ id: LOCALIZED_TEXT.passkey });
  const removalTargetFor = (passkey) => {
    const provider = providerOf(passkey);
    return {
      id: passkey.id,
      label: provider,
      detail: passkey.userAgent ? `${provider} (${passkey.userAgent})` : provider,
      name: fm({ id: LOCALIZED_TEXT.name }),
    };
  };
  const stepUpPasskey = stepUpRemovalId && passkeys.find(({ id }) => id === stepUpRemovalId);
  const removalTarget = passkeyToRemove ?? (stepUpPasskey ? removalTargetFor(stepUpPasskey) : null);
  const passkeyOnly = !hasTwoStepFactor(methods);

  const stepUp = async () => {
    const returnTo = `${accountSettingsSecurity}?${new URLSearchParams({
      [MFA_STEP_UP_PARAM]: MFA_STEP_UP_REMOVE,
      [MFA_STEP_UP_METHOD_PARAM]: removalTarget.id,
    })}`;
    if (!passkeyOnly) {
      navigate(generateMfaStepUpUrl(returnTo));
      return;
    }
    const started = await startMyAccountOperation(MY_ACCOUNT_OPERATIONS.signIn, { returnTo });
    setStartFailed(!started);
  };

  // Adding and removing a passkey sign the customer in again through Auth0. An app that does not
  // keep this page's storage across that round trip stays read-only.
  const canChange = !WebView?.isWebView || appKeepsSignInState();

  // Enterprise and social sign-ins keep their existing passkeys listed but can't add one.
  let addAction = null;
  if (passkeysAvailable) {
    if (!canChange) {
      addAction = (
        <Text component="p" size="sm" color="secondary">
          {fm({ id: LOCALIZED_TEXT.openInBrowser })}
        </Text>
      );
    } else if (passkeys.length >= MAX_PASSKEYS) {
      addAction = (
        <Text component="p" size="sm" color="secondary">
          {fm({ id: LOCALIZED_TEXT.limitReached }, { max: MAX_PASSKEYS })}
        </Text>
      );
    } else {
      addAction = (
        <Button
          data-testid="add-passkey"
          clickstreamProps={{ appSection: 'account', elementName: 'passkeys_add' }}
          onClick={async () => {
            const started = await startMyAccountOperation(MY_ACCOUNT_OPERATIONS.passkey, {
              returnTo: accountSettingsSecurity,
            });
            setStartFailed(!started);
          }}
          size="sm"
          variant="text"
          color="primary"
        >
          {fm({ id: LOCALIZED_TEXT.add })}
        </Button>
      );
    }
  }

  return (
    <TwoStepStyled.Card2fa>
      <div>
        <Heading component="h2" size="lg">
          {fm({ id: LOCALIZED_TEXT.title })}
        </Heading>
        <Text component="p" size="md" color="secondary">
          {fm({ id: LOCALIZED_TEXT.description })}
        </Text>
      </div>
      {passkeys.length > 0 && (
        <TwoStepStyled.MethodList>
          {passkeys.map((passkey) => {
            const provider = providerOf(passkey);
            const added = formatDate(passkey.createdAt);
            const lastUsed = formatDate(passkey.lastAuth);
            return (
              <TwoStepStyled.MethodRow key={passkey.id} data-testid="passkey-row">
                <Styled.PasskeyIcon>
                  <FingerprintRoundedIcon fontSize="small" />
                </Styled.PasskeyIcon>
                <TwoStepStyled.MethodText>
                  <Text component="p" size="md" weight="medium" color="primary">
                    {provider}
                  </Text>
                  {passkey.userAgent && (
                    <Text component="p" size="sm" color="secondary">
                      {passkey.userAgent}
                    </Text>
                  )}
                  {(added || lastUsed) && (
                    <Text component="p" size="sm" color="secondary">
                      {added && fm({ id: LOCALIZED_TEXT.added }, { date: added })}
                      {added && lastUsed && ' · '}
                      {lastUsed && fm({ id: LOCALIZED_TEXT.lastUsed }, { date: lastUsed })}
                    </Text>
                  )}
                </TwoStepStyled.MethodText>
                {canChange && (
                  <IconButton
                    label={fm({ id: LOCALIZED_TEXT.remove }, { method: provider })}
                    data-testid="remove-passkey"
                    onClick={() => setPasskeyToRemove(removalTargetFor(passkey))}
                    icon={<DeleteRoundedIcon />}
                  />
                )}
              </TwoStepStyled.MethodRow>
            );
          })}
        </TwoStepStyled.MethodList>
      )}
      {addAction}
      {removalTarget && (
        <RemoveMfaMethodDialog
          isOpen
          method={removalTarget}
          requireMfa={requireMfa}
          onStepUpRequired={stepUp}
          // Only a passkey-only customer is sent to the passkey sign-in, so their reopened dialog
          // follows one.
          returnedFromPasskeySignIn={passkeyOnly && !passkeyToRemove && Boolean(stepUpPasskey)}
          onClose={() => {
            setPasskeyToRemove(null);
            setStepUpRemovalId(null);
            refetch();
          }}
        />
      )}
      {location.state?.added === MY_ACCOUNT_OPERATIONS.passkey && (
        <Snackbar
          open
          variant="action"
          title={fm({ id: LOCALIZED_TEXT.passkeyAdded })}
          dismissable
          autoHideDuration={6000}
          // Cleared so a reload doesn't show it again.
          onClose={() => navigate(location.pathname, { replace: true, state: null })}
        />
      )}
      {startFailed && (
        <Snackbar
          open
          variant="alert"
          title={fm({ id: LOCALIZED_TEXT.startFailed })}
          dismissable
          autoHideDuration={6000}
          onClose={() => setStartFailed(false)}
        />
      )}
    </TwoStepStyled.Card2fa>
  );
}

export default Passkeys;
